import { select, take, takeLatest, takeEvery, put, call, delay } from 'redux-saga/effects';
import { push as navigateTo } from 'connected-react-router';
import {
    Actions,
    getBuildingsSuccess, getBuildingsFailure,
    getBuildingSuccess, getBuildingFailure,
    deleteBuildingSuccess, deleteBuildingFailure,
    getEventLogSuccess, getEventLogFailure,
    getBuildingThemesSuccess, getBuildingThemesFailure,
    setCredentialsSuccess, setCredentialsFailure,
    setBroadcastSuccess, setBroadcastFailure, getBuildingsRequest, GetBuildingsParams, getBuildingRequest,
} from 'actions/buildingActions';
import i18n from 'i18next';
import { humanizeScheduleRule } from 'logic/scheduleRule';
import { getBuilding } from 'selectors/buildingsSelectors';
import { confirmSaga } from './confirmSaga';
import { Building, Schedule, Theme } from 'shared/models';
import { apiGet, apiPut, apiDelete } from './apiSagas';
import { toError } from 'shared/util/toError';

export function* getBuildingsRequestWatcher() {
    yield takeLatest(Actions.GET_BUILDINGS_REQUEST, getBuildingsSaga);
}

function* getBuildingsSaga(action: ReturnType<typeof getBuildingsRequest>) {
    const params = { ...action.payload };
    const { continueBuildingSearch } = params;
    let continueSearch = true;

    while (continueSearch) {
        const query =
            (Object.keys(params) as (keyof GetBuildingsParams)[])
                .filter(key => params[key] && typeof params[key] !== 'function')
                .map(key => `${key}=${encodeURIComponent(params[key] as string)}`)
                .filter(q => !!q)
                .join('&');

        try {
            // Introduce a small delay if there's a query (to throttle rapid subsequent requests)
            // (e.g. like in the scenario where a user is typing into the search text field)
            if (query) {
                yield delay(400);
            }
            const result: any = (yield call(apiGet, `/api/buildings${query ? '?' + query : ''}`)) as unknown;
            yield put(getBuildingsSuccess(result, params));

            // deliver buildings specificly for copyThemeForm semantic UI onSearch
            // and find out if component wants saga to continue grabbing more buildings
            if (continueBuildingSearch) {
                continueSearch = yield call(continueBuildingSearch, result.buildings);
            }

            const { more } = result;
            if (!more) {
                return;
            }

            // Prepare to call the next page
            params.page = (params.page || 1) + 1;
        }
        catch (e) {
            const err = toError(e);
            yield put(getBuildingsFailure(err));
            if (continueBuildingSearch) {
                yield call(continueBuildingSearch, []);
            }
            return;
        }
    }
}

export function* getBuildingSaga() {
    yield takeEvery(Actions.GET_BUILDING_REQUEST, function* (action: ReturnType<typeof getBuildingRequest>) {
        const urlSlugOrId = action.payload;
        if (!urlSlugOrId) {
            console.error('Missing url slug or building ID');
            return;
        }

        try {
            const cacheBust = ((yield call([ Math, 'random' ])) as number).toString(28).substr(2);
            const buildingAndMore: any = yield call(apiGet, `/api/buildings/${urlSlugOrId}?${cacheBust}`);

            // Patch up the schedule rule dates and times to be strings (for easier consumption by redux-form)
            const { timeZone } = buildingAndMore.building;
            const schedules = Object.keys(buildingAndMore.schedules).map(key => buildingAndMore.schedules[key]);
            schedules.forEach((s: Schedule) => {
                s.rules.forEach(r => humanizeScheduleRule(r, timeZone));
            });

            yield put(getBuildingSuccess(buildingAndMore, { urlSlugOrId }));
        }
        catch (e) {
            const err = toError(e);
            yield put(getBuildingFailure(err, { urlSlugOrId }));
        }
    });
}

export function* getBuildingThemesSaga() {
    while (true) {
        // TODO: takeEvery
        const { payload: buildingId } = yield take(Actions.GET_BUILDING_THEMES_REQUEST);

        try {
            const themes: Theme[] = yield call(apiGet, `/api/buildings/${buildingId}/themes`);
            yield put(getBuildingThemesSuccess({ buildingId, themes }));
        }
        catch (e) {
            const err = toError(e);
            yield put(getBuildingThemesFailure(err, buildingId));
        }
    }
}


export function* deleteBuildingRequestWatcher() {
    yield takeEvery(Actions.DELETE_BUILDING_REQUEST, deleteBuildingSaga);
}

function* deleteBuildingSaga(action: any) {
    const { payload: buildingId } = action;

    const building: Building = yield select(getBuilding, { buildingId });
    const name = building.displayName || '';
    const confirmed: boolean = yield call(confirmSaga, i18n.t('BUILDING.DELETE_CONFIRMATION', { name }));
    if (!confirmed) {
        return;
    }

    try {
        yield call(apiDelete, `/api/buildings/${buildingId}`);
        yield put(deleteBuildingSuccess(buildingId));
        yield put(navigateTo('/buildings'));
    }
    catch (e) {
        const err = toError(e);
        yield put(deleteBuildingFailure(err, buildingId));
    }
}


export function* getEventLogRequestWatcher() {
    yield takeEvery(Actions.GET_EVENT_LOG_REQUEST, getEventLogSaga);
}

function* getEventLogSaga(action: any) {
    const { buildingId, next, maxRows } = action.payload;
    try {
        const queryParams = [
            next ? `next=${next}` : null,
            maxRows ? `maxRows=${maxRows}` : null,
        ].filter(_ => _).join('&');

        const queryString = queryParams ? `?${queryParams}` : '';
        const url = `/api/buildings/${buildingId}/events${queryString}`;

        const { events, hasMore } = yield call(apiGet, url);
        yield put(getEventLogSuccess({ buildingId, events, hasMore }));
    }
    catch (e) {
        const err = toError(e);
        yield put(getEventLogFailure({ err, buildingId }));
        return;
    }
}


export function* setCredentialsRequestWatcher() {
    yield takeEvery(Actions.SET_CREDENTIALS_REQUEST, setCredentialsSaga);
}

function* setCredentialsSaga(action: any) {
    const { buildingId, credentials } = action.payload;
    try {
        yield call(apiPut, `/api/buildings/${buildingId}/credentials`, credentials);
        yield put(setCredentialsSuccess({ buildingId, credentials }));
    }
    catch (e) {
        const err = toError(e);
        yield put(setCredentialsFailure({ buildingId, err }));
    }
}


export function* setBroadcastRequestWatcher() {
    yield takeEvery(Actions.SET_BROADCAST_REQUEST, setBroadcastSaga);
}

function* setBroadcastSaga(action: any) {
    const message = action.payload;
    const { buildingId } = action.meta;
    try {
        yield call(apiPut, `/api/buildings/${buildingId}/broadcast`, { message });
        yield put(setBroadcastSuccess(buildingId));
    }
    catch (e) {
        const err = toError(e);
        yield put(setBroadcastFailure(err, buildingId));
    }
}
